import React, { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import GlobalHeader from '../components/GlobalHeader';
import { useFiscalWeekContext } from '../contexts/FiscalWeekContext';
import { useTenant } from '../contexts/TenantContext';
import { useFiscalWeeks } from '../lib/data-service';
import { formatCurrency, formatPercent, formatNumber } from '../lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { InfoTooltip } from '../components/InfoTooltip';
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from '../components/ui/table';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { 
  ArrowUpDown, 
  ArrowUp, 
  ArrowDown, 
  Download,
  DollarSign,
  Percent,
  TrendingUp,
  AlertTriangle,
  Star
} from 'lucide-react';

interface PricingMarginData {
  week: string;
  cards_total: {
    asp: number | null;
    discount_pct: number | null;
    realization: number | null;
    gm_dollars: number | null;
    gm_percent: number | null;
  };
  top_sku_view: Array<{
    name: string;
    asp: number | null;
    discount_pct: number | null;
    gm_percent: number | null;
  }>;
  rows: Array<{
    sku: string;
    name: string;
    revenue: number;
    units: number;
    asp: number | null;
    retail: number | null;
    discount_pct: number | null;
    realization: number | null;
    asp_change_yoy: number | null;
    gm_dollars: number | null;
    gm_percent: number | null;
    gmroii: number | null;
  }>;
  insights: {
    price_erosion_count: number;
    price_erosion_skus: Array<{
      sku: string;
      name: string;
      discount_pct: number;
      asp: number;
      asp_ly: number | null;
      asp_change: number | null;
      revenue: number;
    }>;
    highest_gmroii: Array<{
      sku: string;
      name: string;
      gmroii: number;
    }>;
  };
  page: number;
  pageSize: number;
  totalRowCount: number;
}

const usePricingMarginData = (fiscalWeek: number | undefined, filters: any, sort: string, sortDir: string, page: number, retailerId: string | null) => {
  return useQuery<PricingMarginData>({
    queryKey: ['pricing-margin', fiscalWeek, filters, sort, sortDir, page, retailerId],
    queryFn: async () => {
      if (!fiscalWeek) {
        throw new Error('Missing fiscal week');
      }
      const params = new URLSearchParams({
        week: fiscalWeek.toString(),
        sort,
        dir: sortDir,
        page: page.toString(),
        pageSize: '50'
      });
      
      // Add retailer_id if selected
      if (retailerId) {
        params.append('retailer_id', retailerId);
      }
      
      if (Object.keys(filters).length > 0) {
        params.append('filters', JSON.stringify(filters));
      }
      
      const response = await fetch(`/api/pricing-margin?${params}`);
      if (!response.ok) throw new Error('Failed to fetch pricing margin data');
      return response.json();
    },
    enabled: !!fiscalWeek
  });
};

export function PricingMargin() {
  const { selectedFiscalWeek, setSelectedFiscalWeek } = useFiscalWeekContext();
  const { selectedRetailerId } = useTenant();
  const { data: fiscalWeeks } = useFiscalWeeks();
  const [searchQuery, setSearchQuery] = useState('');
  const [filters, setFilters] = useState<any>({});
  const [sortField, setSortField] = useState('revenue');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [currentPage, setCurrentPage] = useState(1);

  // Keep fiscal week valid for the selected retailer (ONE SOURCE OF TRUTH with Dashboard behavior).
  useEffect(() => {
    if (fiscalWeeks && fiscalWeeks.length > 0) {
      const weekExists = selectedFiscalWeek && fiscalWeeks.some(w => w.value === selectedFiscalWeek);
      if (!weekExists) {
        setSelectedFiscalWeek(fiscalWeeks[0].value);
      }
    }
  }, [selectedRetailerId, fiscalWeeks, selectedFiscalWeek, setSelectedFiscalWeek]);

  const { data, isLoading, error } = usePricingMarginData(
    selectedFiscalWeek, 
    { ...filters, q: searchQuery }, 
    sortField, 
    sortDirection, 
    currentPage,
    selectedRetailerId
  );

  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
    setCurrentPage(1);
  };

  const handleFilterChange = (key: string, value: any) => {
    setFilters((prev: any) => ({
      ...prev,
      [key]: value
    }));
    setCurrentPage(1);
  };

  const getSortIcon = (field: string) => {
    if (sortField !== field) return <ArrowUpDown className="w-4 h-4" />;
    return sortDirection === 'asc' ? <ArrowUp className="w-4 h-4" /> : <ArrowDown className="w-4 h-4" />;
  };

  const renderProgressBar = (value: number | null, max: number = 1) => {
    if (value === null) return <span className="text-muted-foreground">—</span>;
    const percentage = Math.min(100, (value / max) * 100);
    return (
      <div className="flex items-center gap-2 min-w-[100px]">
        <div className="flex-1 h-2 bg-muted rounded-full overflow-hidden">
          <div 
            className="h-full bg-primary transition-all duration-300" 
            style={{ width: `${percentage}%` }}
          />
        </div>
        <span className="text-xs text-muted-foreground min-w-[40px]">
          {formatPercent(value)}
        </span>
      </div>
    );
  };

  if (error) {
    return (
      <div className="min-h-screen bg-background">
        <GlobalHeader
          title="Pricing & Margin"
          subtitle="Error loading pricing margin data"
        />
        <div className="container mx-auto px-6 py-8">
          <div className="text-center text-red-600">
            Error loading pricing margin data: {(error as Error).message}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <GlobalHeader
        title="Pricing & Margin"
        subtitle="SKU-level pricing realization, discount depth, and margin analysis"
        isLoading={isLoading}
      />
      
      <div className="container mx-auto px-6 py-8 space-y-8 max-w-7xl">

        {isLoading ? (
          <div className="space-y-6">
            {/* Skeleton for cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
              {Array.from({ length: 5 }).map((_, i) => (
                <Card key={i} className="animate-pulse">
                  <CardContent className="p-6">
                    <div className="h-4 bg-muted rounded mb-2"></div>
                    <div className="h-8 bg-muted rounded"></div>
                  </CardContent>
                </Card>
              ))}
            </div>
            {/* Skeleton for table */}
            <Card className="animate-pulse">
              <CardContent className="p-6">
                <div className="h-64 bg-muted rounded"></div>
              </CardContent>
            </Card>
          </div>
        ) : (
          <>
            {/* KPI Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
              <div className="bg-card border border-border rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground flex items-center gap-2">
                      ASP
                      <InfoTooltip content="Average Selling Price = Total Revenue ÷ Total Units. Weighted average across all SKUs." />
                    </p>
                    <p className="text-2xl font-bold text-foreground">
                      {data?.cards_total.asp ? formatCurrency(data.cards_total.asp) : '—'}
                    </p>
                  </div>
                  <DollarSign className="w-5 h-5 text-primary" />
                </div>
              </div>

              <div className="bg-card border border-border rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground flex items-center gap-2">
                      Discount %
                      <InfoTooltip content="1 - (ASP ÷ Retail Price). Revenue-weighted average across SKUs with valid retail prices." />
                    </p>
                    <p className="text-2xl font-bold text-foreground">
                      {data?.cards_total.discount_pct ? formatPercent(data.cards_total.discount_pct) : '—'}
                    </p>
                  </div>
                  <Percent className="w-5 h-5 text-orange-500" />
                </div>
              </div>

              <div className="bg-card border border-border rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground flex items-center gap-2">
                      Realization
                      <InfoTooltip content="ASP ÷ Retail Price. Shows actual selling price as percentage of retail. Revenue-weighted average." />
                    </p>
                    <p className="text-2xl font-bold text-foreground">
                      {data?.cards_total.realization ? formatPercent(data.cards_total.realization) : '—'}
                    </p>
                  </div>
                  <TrendingUp className="w-5 h-5 text-green-500" />
                </div>
              </div>

              <div className="bg-card border border-border rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground flex items-center gap-2">
                      GM$ (Est.)
                      <InfoTooltip content="Gross Margin Dollars = Revenue - (Cost × Units). Estimated using purchase cost average. Total across all SKUs." />
                    </p>
                    <p className="text-2xl font-bold text-foreground">
                      {data?.cards_total.gm_dollars ? formatCurrency(data.cards_total.gm_dollars, 0) : '—'}
                    </p>
                  </div>
                  <DollarSign className="w-5 h-5 text-blue-500" />
                </div>
              </div>

              <div className="bg-card border border-border rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground flex items-center gap-2">
                      GM% (Est.)
                      <InfoTooltip content="Gross Margin % = GM$ ÷ Revenue. Estimated using purchase cost average. Revenue-weighted average." />
                    </p>
                    <p className="text-2xl font-bold text-foreground">
                      {data?.cards_total.gm_percent ? formatPercent(data.cards_total.gm_percent) : '—'}
                    </p>
                  </div>
                  <Percent className="w-5 h-5 text-purple-500" />
                </div>
              </div>
            </div>

            {/* Insights Section */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-card border border-border rounded-lg p-4">
                <div className="flex items-center gap-2 mb-4">
                  <AlertTriangle className="h-5 w-5 text-orange-500" />
                  <h3 className="text-lg font-semibold">Price Erosion Alert</h3>
                  <InfoTooltip content="SKUs losing pricing power: Discount >10% AND Average Selling Price declining vs last year. These products are being heavily discounted yet still not moving well, indicating potential overstock or declining demand." />
                </div>
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <div className="text-2xl font-bold text-orange-600">
                        {data?.insights.price_erosion_count || 0}
                      </div>
                      <p className="text-sm text-muted-foreground">
                        SKUs with discount &gt;10% and negative YoY ASP change
                      </p>
                    </div>
                  </div>
                  {data?.insights.price_erosion_skus && data.insights.price_erosion_skus.length > 0 ? (
                    <div className="space-y-2 max-h-48 overflow-y-auto">
                      {data.insights.price_erosion_skus.slice(0, 10).map((sku) => (
                        <div key={sku.sku} className="flex items-center justify-between text-sm p-2 bg-orange-50 dark:bg-orange-950/20 rounded border border-orange-200 dark:border-orange-800">
                          <div className="flex-1 min-w-0">
                            <div className="font-medium truncate">{sku.name}</div>
                            <div className="text-xs text-muted-foreground">
                              {sku.sku} • Discount: {formatPercent(sku.discount_pct)} • 
                              ASP Change: {sku.asp_change ? formatCurrency(sku.asp_change) : 'N/A'}
                            </div>
                          </div>
                        </div>
                      ))}
                      {data.insights.price_erosion_count > 10 && (
                        <div className="text-xs text-muted-foreground text-center pt-2">
                          +{data.insights.price_erosion_count - 10} more SKUs
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="text-sm text-muted-foreground text-center py-4 bg-green-50 dark:bg-green-950/20 rounded border border-green-200 dark:border-green-800">
                      ✓ No price erosion alerts
                    </div>
                  )}
                </div>
              </div>

              <div className="bg-card border border-border rounded-lg p-4">
                <div className="flex items-center gap-2 mb-4">
                  <Star className="h-5 w-5 text-yellow-500" />
                  <h3 className="text-lg font-semibold">Top GMROII Performers</h3>
                  <InfoTooltip content="Gross Margin Return on Inventory Investment. Formula: (Gross Margin $ × 52 weeks) ÷ (Unit Cost × EOH Units). Shows which SKUs generate the best margin per dollar invested in inventory. Higher is better. Example: 2.39x means every $1 in inventory generates $2.39 in annual gross margin." />
                </div>
                <div className="space-y-2">
                  {data?.insights.highest_gmroii.slice(0, 3).map((sku, index) => (
                    <div key={sku.sku} className="flex items-center justify-between text-sm">
                      <span className="truncate flex-1">#{index + 1} {sku.name}</span>
                      <Badge variant="secondary">
                        {formatNumber(sku.gmroii, 2)}x
                      </Badge>
                    </div>
                  ))}
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="w-full mt-2"
                    onClick={() => handleFilterChange('gmroii_top', true)}
                  >
                    View All Top GMROII
                  </Button>
                </div>
              </div>
            </div>

            {/* Filters and Search */}
            <div className="bg-card border border-border rounded-lg p-4">
              <h3 className="text-lg font-semibold mb-4">Filters & Search</h3>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div>
                    <label className="text-sm font-medium mb-2 block">Search SKU/Name</label>
                    <Input
                      placeholder="Search products..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                    />
                  </div>
                  
                  <div>
                    <label className="text-sm font-medium mb-2 block">Discount Bands</label>
                    <Select onValueChange={(value) => handleFilterChange('discount_bands', value === 'all' ? [] : [value])}>
                      <SelectTrigger>
                        <SelectValue placeholder="All discounts" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Discounts</SelectItem>
                        <SelectItem value="0-10">0-10%</SelectItem>
                        <SelectItem value="10-20">10-20%</SelectItem>
                        <SelectItem value=">20">&gt;20%</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div>
                    <label className="text-sm font-medium mb-2 block">GM Bands</label>
                    <Select onValueChange={(value) => handleFilterChange('gm_bands', value === 'all' ? [] : [value])}>
                      <SelectTrigger>
                        <SelectValue placeholder="All GM%" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All GM%</SelectItem>
                        <SelectItem value="low">Low (Bottom 33%)</SelectItem>
                        <SelectItem value="med">Medium (Middle 33%)</SelectItem>
                        <SelectItem value="high">High (Top 33%)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="flex items-end">
                    <Button variant="outline" className="w-full">
                      <Download className="w-4 h-4 mr-2" />
                      Export CSV
                    </Button>
                  </div>
                </div>
              </div>

            {/* Main Table */}
            <div className="bg-card border border-border rounded-lg p-4">
              <div className="mb-4">
                <h3 className="text-lg font-semibold">SKU Pricing & Margin Analysis</h3>
                <div className="text-sm text-muted-foreground">
                  Showing {data?.rows.length || 0} of {data?.totalRowCount || 0} SKUs
                </div>
              </div>
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="min-w-[200px] py-4">SKU</TableHead>
                        <TableHead className="cursor-pointer py-4" onClick={() => handleSort('revenue')}>
                          <div className="flex items-center gap-2">
                            Revenue {getSortIcon('revenue')}
                          </div>
                        </TableHead>
                        <TableHead className="cursor-pointer py-4" onClick={() => handleSort('units')}>
                          <div className="flex items-center gap-2">
                            Units {getSortIcon('units')}
                          </div>
                        </TableHead>
                        <TableHead className="cursor-pointer py-4" onClick={() => handleSort('asp')}>
                          <div className="flex items-center gap-2">
                            ASP {getSortIcon('asp')}
                          </div>
                        </TableHead>
                        <TableHead className="py-4">Retail</TableHead>
                        <TableHead className="cursor-pointer py-4" onClick={() => handleSort('discount_pct')}>
                          <div className="flex items-center gap-2">
                            Discount % {getSortIcon('discount_pct')}
                          </div>
                        </TableHead>
                        <TableHead className="py-4">ΔASP YoY</TableHead>
                        <TableHead className="cursor-pointer py-4" onClick={() => handleSort('gm_dollars')}>
                          <div className="flex items-center gap-2">
                            GM$ {getSortIcon('gm_dollars')}
                          </div>
                        </TableHead>
                        <TableHead className="cursor-pointer py-4" onClick={() => handleSort('gm_percent')}>
                          <div className="flex items-center gap-2">
                            GM% {getSortIcon('gm_percent')}
                          </div>
                        </TableHead>
                        <TableHead className="cursor-pointer py-4" onClick={() => handleSort('gmroii')}>
                          <div className="flex items-center gap-2">
                            GMROII {getSortIcon('gmroii')}
                          </div>
                        </TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {data?.rows.map((row) => (
                        <TableRow key={row.sku} className="hover:bg-muted/50">
                          <TableCell className="font-medium py-4">
                            <div>
                              <div className="font-semibold">{row.sku}</div>
                              <div className="text-sm text-muted-foreground truncate">
                                {row.name}
                              </div>
                            </div>
                          </TableCell>
                          <TableCell className="py-4">{formatCurrency(row.revenue, 0)}</TableCell>
                          <TableCell className="py-4">{formatNumber(row.units, 0)}</TableCell>
                          <TableCell className="py-4">
                            {row.asp ? formatCurrency(row.asp) : '—'}
                          </TableCell>
                          <TableCell className="text-muted-foreground py-4">
                            {row.retail ? formatCurrency(row.retail) : '—'}
                          </TableCell>
                          <TableCell className="py-4">
                            {renderProgressBar(row.discount_pct, 0.4)}
                          </TableCell>
                          <TableCell className="py-4">
                            {row.asp_change_yoy ? (
                              <Badge variant={row.asp_change_yoy >= 0 ? "default" : "destructive"}>
                                {row.asp_change_yoy >= 0 ? '+' : ''}{formatCurrency(row.asp_change_yoy)}
                              </Badge>
                            ) : '—'}
                          </TableCell>
                          <TableCell className="py-4">
                            {row.gm_dollars ? formatCurrency(row.gm_dollars, 0) : '—'}
                          </TableCell>
                          <TableCell className="py-4">
                            {row.gm_percent ? formatPercent(row.gm_percent) : '—'}
                          </TableCell>
                          <TableCell className="py-4">
                            {row.gmroii ? formatNumber(row.gmroii, 2) + 'x' : '—'}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>

                {/* Pagination */}
                {data && data.totalRowCount > 50 && (
                  <div className="flex items-center justify-between mt-6">
                    <div className="text-sm text-muted-foreground">
                      Page {currentPage} of {Math.ceil(data.totalRowCount / 50)}
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={currentPage === 1}
                        onClick={() => setCurrentPage(currentPage - 1)}
                      >
                        Previous
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={currentPage >= Math.ceil(data.totalRowCount / 50)}
                        onClick={() => setCurrentPage(currentPage + 1)}
                      >
                        Next
                      </Button>
                    </div>
                  </div>
                )}
              </div>
          </>
        )}
      </div>
    </div>
  );
}
